import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Form } from '@/components/ui/form'
import { ForensicsForm } from '../form'
import { useCreateForensicsEnvironment, useUpdateForensicsEnvironment } from '../hooks'
import { forensicsEnvironmentSchema, type ForensicsEnvironmentFormData } from '../schema'
import type { ForensicsEnvironment } from '@/types/entities'

interface ForensicsFormDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    environment: ForensicsEnvironment | null
}

export function ForensicsFormDialog({ open, onOpenChange, environment }: ForensicsFormDialogProps) {
    const isEditing = !!environment
    const createEnv = useCreateForensicsEnvironment()
    const updateEnv = useUpdateForensicsEnvironment()

    const form = useForm<ForensicsEnvironmentFormData>({
        resolver: zodResolver(forensicsEnvironmentSchema) as any,
        defaultValues: {
            name: '',
            vaultId: 0,
            securityGroupId: '',
            awsAccountId: '',
            description: '',
            servers: [],
        },
    })

    useEffect(() => {
        if (open) {
            if (environment) {
                form.reset({
                    name: environment.name,
                    vaultId: environment.vaultId,
                    securityGroupId: environment.securityGroupId,
                    awsAccountId: environment.awsAccountId,
                    description: environment.description || '',
                    servers: environment.servers?.map(s => ({
                        computeObjectId: s.computeObjectId,
                        computeObjectVersionId: s.computeObjectVersionId || undefined,
                    })) || [],
                })
            } else {
                form.reset({
                    name: '',
                    vaultId: 0,
                    securityGroupId: '',
                    awsAccountId: '',
                    description: '',
                    servers: [],
                })
            }
        }
    }, [open, environment, form])

    const onSubmit = async (data: ForensicsEnvironmentFormData) => {
        try {
            if (isEditing && environment) {
                await updateEnv.mutateAsync({
                    id: environment.id,
                    data: {
                        name: data.name,
                        securityGroupId: data.securityGroupId,
                        awsAccountId: data.awsAccountId,
                        description: data.description,
                        servers: data.servers,
                    },
                })
            } else {
                await createEnv.mutateAsync(data)
            }
            onOpenChange(false)
        } catch {
            // Error handling in hooks
        }
    }

    const isLoading = createEnv.isPending || updateEnv.isPending

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>
                        {isEditing ? 'Edit Forensics Environment' : 'Create Forensics Environment'}
                    </DialogTitle>
                    <DialogDescription>
                        {isEditing
                            ? 'Update the forensics environment configuration.'
                            : 'Configure a new isolated forensics environment.'}
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit as any)} className="space-y-6">
                        <ForensicsForm form={form as any} isEditing={isEditing} />

                        <div className="flex justify-end space-x-2 pt-4">
                            <button
                                type="button"
                                onClick={() => onOpenChange(false)}
                                disabled={isLoading}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
                            >
                                {isLoading ? 'Saving...' : isEditing ? 'Update' : 'Create'}
                            </button>
                        </div>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}
